import { AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { ScrapCauseDto } from 'src/app/shared/dto/scrap-cause.dto';
import { ScrapReasonDto } from 'src/app/shared/dto/scrap-reason.dto';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { ScrapCauseService } from '../../services/scrap-cause.service';
import { ScrapReasonService } from '../../services/scrap-reason.service';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormDirective, NzFormItemComponent, NzFormControlComponent, NzFormModule } from 'ng-zorro-antd/form';
import { NzRowDirective, NzColDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { DrawerMode } from 'src/app/core/enums/drawer-mode';
import { DRAWER_WIDTH_CHANGE_PLACEMENT } from 'src/app/core/constants/components';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { DefaultComponent } from 'src/app/shared/services/view.service';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
    selector: 'app-scrap-reasons',
    standalone: true,
    imports: [
        NzPageHeaderModule,
        NzSpaceModule,
        NzDividerModule,
        NzDrawerModule,
        NzDatePickerModule,
        FormsModule,
        NzMessageModule,
        NzTableModule,
        NzSelectModule,
        NzInputDirective,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzInputDirective,
        NzFormModule,
        NzIconModule,
        NzToolTipModule,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './scrap-reasons.component.html',
    styleUrl: './scrap-reasons.component.scss'
})

export class ScrapReasonsComponent extends DefaultComponent implements OnInit, AfterViewInit, OnDestroy {

    fieldsReadOnly: boolean = true;
    drawerTitle: string = 'Placeholder';
    drawerMode!: DrawerMode;
    drawerVisible: boolean = false;
    reasonSelected: ScrapReasonDto = new ScrapReasonDto();
    scrapReasons: ScrapReasonDto[] = [];
    currentUser: UserDto = new UserDto();
    causesDataSet: ScrapCauseDto[] = [];
    selectedCauses: ScrapCauseDto[] = [];
    isLoading: boolean = true;
    isSaving: boolean = false;

    innerWidth: number = 0;
    DRAWER_WIDTH_CHANGE_PLACEMENT = DRAWER_WIDTH_CHANGE_PLACEMENT;

    validateForm: FormGroup<{
        text: FormControl<string>;
        scrapCauses: FormControl<ScrapCauseDto[]>;
    }>;

    private readonly reasonsService = inject(ScrapReasonService);
    private readonly causesService = inject(ScrapCauseService);
    private readonly message = inject(NzMessageService);
    private readonly userLoggedService = inject(UserLoggedService);
    private readonly fb = inject(NonNullableFormBuilder);

    private unsubscribe$ = new Subject<void>();

    constructor() {
        super('scrapReasons', '');
        this.validateForm = this.fb.group({
            text: ['', [Validators.required]],
            scrapCauses: new FormControl<ScrapCauseDto[]>([], { nonNullable: true })
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.innerWidth = window.innerWidth;
    }

    async ngOnInit() {
        this.currentUser = this.userLoggedService.userLogged;
        this.innerWidth = window.innerWidth;

        await this.reasonsService.setupWebSocket();
        this.reasonsService.scrapReasons$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (reasons: ScrapReasonDto[]) => {
                    this.scrapReasons = reasons;
                    this.reasonSelected = reasons.find(r => r.id === this.reasonSelected.id) || new ScrapReasonDto();
                    this.selectedCauses = this.reasonSelected.scrapCauses as ScrapCauseDto[];
                },
                error: () => {
                    this.message.error('Error cargando razones de tara');
                }
            });

        this.causesService.setupWebSocket();
        this.causesService.scrapCauses$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (causes: ScrapCauseDto[]) => {
                    this.causesDataSet = causes;
                },
                error: () => {
                    this.message.error('Error cargando causas de tara');
                }
            });

        this.isLoading = false;
    }

    ngAfterViewInit() {
        this.innerWidth = window.innerWidth;
    }

    ngOnDestroy(): void {
        this.reasonsService.disconnectWebSocket();
        this.causesService.disconnectWebSocket();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    create() {
        this.reasonSelected = { id: 0, text: '' };
        this.selectedCauses = [];
        this.drawerMode = DrawerMode.CREATE;
        this.drawerTitle = 'Creando nueva razón de tara';
        this.fieldsReadOnly = false;
        this.validateForm.enable();
        this.openDrawer();
    }

    view(data: ScrapReasonDto) {
        this.validateForm.patchValue(data);
        this.validateForm.disable();
        this.reasonSelected = data;
        this.drawerMode = DrawerMode.VIEW;
        this.drawerTitle = 'Viendo razón ' + this.reasonSelected?.text;
        this.fieldsReadOnly = true;
        this.setData();
        this.openDrawer();
    }

    edit(data: ScrapReasonDto) {
        this.reasonSelected = data;
        this.validateForm.patchValue(data);
        this.validateForm.enable();
        this.drawerMode = DrawerMode.EDIT;
        this.drawerTitle = 'Editando razón ' + this.reasonSelected?.text;
        this.fieldsReadOnly = false;
        this.setData();
        this.openDrawer();
    }

    setData() {
        const scrapCauses: ScrapCauseDto[] = this.reasonSelected.scrapCauses as ScrapCauseDto[];

        this.selectedCauses = this.causesDataSet.filter((c) =>
            scrapCauses.some((s) => s.id === c.id)
        );
    }

    cancelEditing() {
        this.closeDrawer();
    }

    delete(id: number) {
        if (
            window.confirm(
                '¿Estás seguro de que quieres eliminar esta razón de tara? Esta acción no se puede deshacer.',
            )
        ) {
            this.reasonsService.delete(id).subscribe({
                next: (reason: ScrapCauseDto) => {
                    try {
                        if (reason == undefined) {
                            this.message.create('error', `No se ha podido eliminar la razón de tara, razón de tara no encontrada`);

                            return;
                        }

                        const index = this.scrapReasons.findIndex((p) => p.id === id);

                        if (index < 0) {
                            this.message.create('error', `No se ha podido eliminar la razón ${reason.text} con id ${id}, razón de tara no encontrada`);

                            return;
                        }

                        this.scrapReasons.splice(index, 1);
                        const data = [...this.scrapReasons];
                        this.scrapReasons = data;

                        this.message.create('success', 'Razón de tara eliminado correctamente',);
                    } catch (error: any) {
                        this.message.create('error', `Error eliminando razón de tara. Error: ${error.message}`);
                    }
                },
                error: (error: any) => {
                    this.message.create('error', `Error eliminando razón de tara, error de servidor. Error: ${error.message}`);
                },
            });
        } else {
            console.log('Cancelando eliminación de razón de tara ' + id);
        }
    }

    openDrawer() {
        this.drawerVisible = true;
    }

    closeDrawer() {
        this.reasonSelected = new ScrapReasonDto();
        this.validateForm.reset();
        this.drawerVisible = false;
    }

    submitForm() {
        this.isSaving = true;

        const customReasonDto: ScrapReasonDto = {
            id: this.reasonSelected.id,
            text: this.validateForm.controls.text.value ? this.validateForm.controls.text.value : '',
            scrapCauses: this.validateForm.controls.scrapCauses.value ? this.validateForm.controls.scrapCauses.value : [],
        };

        if (!this.validForm(customReasonDto)) {
            this.isSaving = false;
            return;
        }

        if (this.drawerMode === DrawerMode.CREATE) {
            this.reasonsService.save(customReasonDto).subscribe({
                next: (reason: ScrapReasonDto) => {
                    try {
                        if (reason == undefined) {
                            this.isSaving = false;
                            throw new Error('Error creating scrap reason');
                        }

                        this.scrapReasons.push(reason);

                        const data = [...this.scrapReasons];
                        this.scrapReasons = data;

                        this.isSaving = false;
                        this.message.create('success', 'Razón de tara creada correctamente');

                        this.closeDrawer();
                    } catch (error: any) {
                        this.isSaving = false;
                        this.message.create('error', `Error creando razón de tara. Error: ${error.message}`);
                    }
                },
                error: (error: any) => {
                    this.isSaving = false;
                    this.message.create('error', `Error creando razón de tara, error de servidor. Error: ${error.message}`);
                }
            });
        } else if (this.drawerMode === DrawerMode.EDIT) {
            this.reasonsService.update(customReasonDto.id, customReasonDto).subscribe({
                next: (reason: ScrapCauseDto) => {
                    try {
                        if (reason == undefined) {
                            this.isSaving = false;
                            this.message.create('error', 'Error actualizando las razones de tara');

                            return;
                        }

                        const index = this.scrapReasons.findIndex(
                            (r) => r.id === reason.id,
                        );

                        if (index < 0) {
                            this.isSaving = false;
                            this.message.create('error', `No se ha podido actualizar la razón ${customReasonDto.text} con id ${customReasonDto.id}, razón de tara no encontrada`);

                            return;
                        }

                        this.scrapReasons[index] = reason;
                        const data = [...this.scrapReasons];
                        this.scrapReasons = data;

                        this.isSaving = false;
                        this.message.create('success', 'Razón de tara actualizada correctamente');

                        this.closeDrawer();
                    } catch (error: any) {
                        this.isSaving = false;
                        this.message.create('error', `Error actualizando razón de tara. Error: ${error.message}`);
                    }
                },
                error: (error: any) => {
                    this.isSaving = false;
                    this.message.create('error', `Error actualizando razón de tara, error de servidor. Error: ${error.message}`);
                }
            });
        }
    }

    validForm(cause: ScrapReasonDto): boolean {
        if (!cause.text) {
            this.message.create('error', 'El texto de la razón de tara es obligatorio',);
            return false;
        }
        return true;
    }

    compareCauses = (o1: any, o2: any): boolean => {
        return o1 && o2 ? o1.id === o2.id : o1 === o2;
    };
}
